import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, CalendarDays, Mail, Phone, Pencil, Trash2, Folder, Images, ListChecks,
} from "lucide-react";
import { StatusBadge } from "./Dashboard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function GalleryClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [creatingGallery, setCreatingGallery] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/gallery-clients/${clientId}`);
      setClient(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  async function deleteClient() {
    const hasCollections = (client.collections || []).length > 0;
    const msg = hasCollections
      ? `Delete client "${client.name}"?\n\nOK = also delete all ${client.collections.length} collection(s) and photos.\nCancel = keep collections (they become unassigned).`
      : `Delete client "${client.name}"?`;
    const cascadeChoice = window.confirm(msg);
    if (hasCollections && !cascadeChoice) {
      // user chose Cancel — but offer keep-collections path
      if (!window.confirm("Delete the client folder but KEEP the collections as unassigned?")) return;
      try {
        await api.delete(`/admin/gallery-clients/${clientId}?cascade=false`);
        toast.success("Client deleted, collections preserved");
        navigate("/admin/galleries");
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      return;
    }
    try {
      await api.delete(`/admin/gallery-clients/${clientId}?cascade=${hasCollections ? "true" : "false"}`);
      toast.success("Client deleted");
      navigate("/admin/galleries");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  if (loading || !client) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="client-detail-page">
      <Link to="/admin/galleries" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground" data-testid="back-to-galleries">
        <ArrowLeft className="w-3.5 h-3.5" /> All clients
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2"><Folder className="w-3.5 h-3.5" /> Client folder</div>
          <h1 className="font-serif text-4xl font-light" data-testid="client-name">{client.name}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
            {client.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {client.email}</span>}
            {client.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {client.phone}</span>}
            {client.event_date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {new Date(client.event_date).toLocaleDateString("en-US", { dateStyle: "medium" })}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEditing(true)} className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em]" data-testid="edit-client-btn">
            <Pencil className="w-4 h-4" /> Edit
          </button>
          <button onClick={deleteClient} className="inline-flex items-center gap-2 px-4 h-10 border border-destructive/40 text-destructive hover:bg-destructive/5 text-xs uppercase tracking-[0.18em]" data-testid="delete-client-btn">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button onClick={() => setCreatingGallery(true)} className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90" data-testid="new-collection-for-client-btn">
            <Plus className="w-4 h-4" /> New collection
          </button>
        </div>
      </div>

      {client.notes && (
        <div className="bg-muted px-5 py-4 text-sm whitespace-pre-wrap" data-testid="client-notes">{client.notes}</div>
      )}

      <div>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif text-2xl">Collections ({client.collections.length})</h2>
        </div>
        {client.collections.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center text-muted-foreground">
            <Images className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" />
            <div className="font-serif text-2xl text-foreground/60">No collections yet</div>
            <div className="text-sm mt-1">Add this client's first collection.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="collections-grid">
            {client.collections.map((g) => (
              <button
                key={g.id}
                onClick={() => navigate(`/admin/galleries/${g.id}`)}
                className="text-left bg-card border border-border hover:border-foreground/60 group transition-colors"
                data-testid={`collection-card-${g.id}`}
              >
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                  {g.cover_image ? (
                    <img src={`${BACKEND}${g.cover_image}`} alt={g.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/40"><Images className="w-8 h-8" /></div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-serif text-lg truncate flex-1">{g.name}</div>
                    <StatusBadge value={g.status} />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Images className="w-3 h-3" />{g.photo_count} photos</span>
                    {g.selection_count > 0 && <span className="inline-flex items-center gap-1"><ListChecks className="w-3 h-3" />{g.selection_count}</span>}
                  </div>
                  {g.category_tags?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {g.category_tags.map((t) => (
                        <span key={t} className="px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-muted">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <EditClientDialog
        open={editing}
        onClose={() => setEditing(false)}
        client={client}
        onSaved={() => { setEditing(false); load(); }}
      />
      <NewCollectionForClientDialog
        open={creatingGallery}
        onClose={() => setCreatingGallery(false)}
        client={client}
        onCreated={(galleryId) => { setCreatingGallery(false); navigate(`/admin/galleries/${galleryId}`); }}
      />
    </div>
  );
}

function EditClientDialog({ open, onClose, client, onSaved }) {
  const [draft, setDraft] = useState({});
  useEffect(() => {
    if (open) setDraft({
      name: client.name, email: client.email || "", phone: client.phone || "",
      event_date: client.event_date || "", notes: client.notes || "",
    });
  }, [open, client]);

  async function save() {
    if (!draft.name?.trim()) return toast.error("Name is required");
    try {
      await api.patch(`/admin/gallery-clients/${client.id}`, {
        name: draft.name.trim(),
        email: draft.email || undefined,
        phone: draft.phone || undefined,
        event_date: draft.event_date || undefined,
        notes: draft.notes,
      });
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-light">Edit client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <In label="Name *" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="edit-client-name" />
          <div className="grid grid-cols-2 gap-3">
            <In label="Email" value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} testid="edit-client-email" />
            <In label="Phone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} testid="edit-client-phone" />
          </div>
          <In label="Event date" type="date" value={draft.event_date} onChange={(v) => setDraft({ ...draft, event_date: v })} testid="edit-client-event-date" />
          <label className="block">
            <span className="eyebrow">Notes</span>
            <textarea rows={3} value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="mt-1 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground text-sm" data-testid="edit-client-notes" />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-5 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60">Cancel</button>
          <button onClick={save} className="px-5 h-10 text-xs uppercase tracking-[0.18em] bg-primary text-primary-foreground" data-testid="save-client-btn">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewCollectionForClientDialog({ open, onClose, client, onCreated }) {
  const [draft, setDraft] = useState({ name: "", password: "", download_password: "", included_edits: 0, extra_edit_price: 35, max_selections: 0, welcome_message: "", category_tags: "", cover_layout: "center" });
  useEffect(() => { if (!open) setDraft({ name: "", password: "", download_password: "", included_edits: 0, extra_edit_price: 35, max_selections: 0, welcome_message: "", category_tags: "", cover_layout: "center" }); }, [open]);

  async function submit() {
    if (!draft.name.trim()) return toast.error("Collection name is required");
    if (!draft.password.trim()) return toast.error("View password is required");
    try {
      const tags = draft.category_tags.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/galleries", {
        name: draft.name.trim(),
        client_id: client.id,
        customer_name: client.name,
        customer_email: client.email || undefined,
        password: draft.password.trim(),
        download_password: draft.download_password.trim() || undefined,
        included_edits: Number(draft.included_edits) || 0,
        extra_edit_price: Number(draft.extra_edit_price) || 35,
        max_selections: Number(draft.max_selections) || 0,
        welcome_message: draft.welcome_message || undefined,
        cover_layout: draft.cover_layout,
        category_tags: tags,
      });
      toast.success("Collection created");
      onCreated(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">New collection for {client?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <In label="Collection name *" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="new-coll-name" placeholder="e.g. Pre Selections" />
          <div className="grid grid-cols-2 gap-3">
            <In label="View password *" value={draft.password} onChange={(v) => setDraft({ ...draft, password: v })} testid="new-coll-password" />
            <In label="Download password" value={draft.download_password} onChange={(v) => setDraft({ ...draft, download_password: v })} testid="new-coll-dlpw" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <In label="Included edits" type="number" value={draft.included_edits} onChange={(v) => setDraft({ ...draft, included_edits: v })} testid="new-coll-edits" />
            <In label="Extra edit ($)" type="number" value={draft.extra_edit_price} onChange={(v) => setDraft({ ...draft, extra_edit_price: v })} testid="new-coll-extra" />
            <In label="Max selections" type="number" value={draft.max_selections} onChange={(v) => setDraft({ ...draft, max_selections: v })} testid="new-coll-max" />
          </div>
          <In label="Category tags (comma-sep)" value={draft.category_tags} onChange={(v) => setDraft({ ...draft, category_tags: v })} testid="new-coll-tags" />
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-5 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60">Cancel</button>
          <button onClick={submit} className="px-5 h-10 text-xs uppercase tracking-[0.18em] bg-primary text-primary-foreground" data-testid="create-new-coll-btn">Create</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function In({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        data-testid={testid}
      />
    </label>
  );
}
